import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import styled from 'styled-components';
import Button from 'react-bootstrap/Button';

import Select from '@components/form/Select';
import Alert from '@components/alert/Alert';
import Input from '@components/form/Input';
import Toggle from '@components/form/Toggle';

import {
	adminGetOrganizationV2,
	adminCreateOrganizationV2,
	adminUpdateOrganizationV2,
	adminDeleteOrganizationV2,
	adminEnableOrganizationProductV2,
	adminDisableOrganizationProductV2,
	adminPutOrganizationMemberV2,
	adminDeleteOrganizationMemberV2,
	adminGetUsersV2,
} from '@utils/api';
import { PRODUCTS } from '@utils/constants';
import { ROLES } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import { useConfirm } from '@contexts/Confirm.context';
import {
	adminProjectsUrl,
	adminBillingUrl,
} from '@routes/adminRoutes/utils/helpers';
import useCanViewBilling from '@routes/adminRoutes/utils/useCanViewBilling';
import useDebounce from '@hooks/useDebounce';
import AdminView from '@components/layout/views/AdminView';
import OrganizationDetailsFormFields from '@components/forms/OrganizationDetailsFormFields';

const Section = styled.section`
	border: 1px solid ${props => props.theme.colors.border};
	border-radius: 4px;
	padding: 1rem 1.25rem;
	margin-bottom: 1.5rem;
`;

const DangerSection = styled(Section)`
	border-color: ${props => props.theme.colors.red ?? '#dc3545'};
`;

const DangerBar = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;

	p {
		margin: 0;
	}
`;

const QuickLinks = styled.div`
	display: flex;
	gap: 1.5rem;
	margin-bottom: 1.5rem;

	a {
		color: ${props => props.theme.colors.lightGreen ?? '#5cb85c'};
		text-decoration: none;
		&:hover {
			text-decoration: underline;
		}
	}
`;

const SectionHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1rem;

	h2 {
		font-size: 1.1rem;
		margin: 0;
	}
`;

const SectionActions = styled.div`
	display: flex;
	gap: 0.5rem;
	margin-top: 1rem;
`;

const ProductRow = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.5rem 0;
	border-bottom: 1px solid ${props => props.theme.colors.border};

	&:last-child {
		border-bottom: none;
	}
`;

const ProductName = styled.span`
	font-weight: 400;
`;

const WmsParam = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 0.5rem;
	padding: 0.25rem 0 0.75rem 1rem;
`;

const MemberRow = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	padding: 0.4rem 0;
	border-bottom: 1px solid ${props => props.theme.colors.border};

	&:last-child {
		border-bottom: none;
	}
`;

const MemberEmail = styled.span`
	flex: 1;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const RoleSelect = styled.div`
	width: 140px;
	flex: 0 0 auto;

	/* The shared Select is sized for floating-label form fields (tall top
	   padding + 1rem bottom margin). This inline role control has no label, so
	   strip that extra height to keep the member row compact. */
	.react-select__control {
		padding: 0 0.25rem;
		min-height: 38px;
	}
`;

const AddMemberBar = styled.div`
	margin-top: 1rem;
`;

const AddMemberRow = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 0.5rem;
	margin-top: 1rem;

	> div:first-child {
		flex: 1;
		max-width: 360px;
	}

	/* The search field carries the shared Select's floating-label top padding
	   (~1.25rem), so its input sits lower than the top of the control. Offset
	   the button by the same amount so it lines up with the field. */
	> button {
		margin-top: 1.25rem;
	}
`;

// Compact read-only label/value grid — far denser than stacked form groups.
const DetailGrid = styled.dl`
	display: grid;
	grid-template-columns: 180px 1fr;
	gap: 0.35rem 1rem;
	margin: 0;

	dt {
		color: ${props => props.theme.colors.textAccent};
		font-weight: 400;
	}
	dd {
		margin: 0;
	}
`;

const DETAIL_FIELDS = [
	{ key: 'name', label: 'Name' },
	{ key: 'description', label: 'Description' },
	{ key: 'org_no', label: 'Organization number' },
	{ key: 'address', label: 'Address' },
	{ key: 'postal_code', label: 'Postal code' },
	{ key: 'city', label: 'City' },
	{ key: 'country', label: 'Country' },
];

/** Normalizes attribute form values into the shape the v2 API expects. */
const normalizeDetails = data => ({
	name: data.name,
	description: data.description || null,
	org_no: data.org_no === '' ? null : data.org_no,
	address: data.address || null,
	postal_code: data.postal_code === '' ? null : data.postal_code,
	city: data.city || null,
	country: data.country || null,
	project_tags: data.project_tags || [],
});

const Organization = () => {
	const { organization_uuid } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const canViewBilling = useCanViewBilling();

	const isEdit = !!organization_uuid;

	// In create mode we start in the form; in edit mode we start read-only.
	const [editing, setEditing] = useState(!isEdit);

	const {
		isLoading,
		isError,
		data: organization,
		error,
	} = useQuery({
		queryKey: ['organization', organization_uuid],
		queryFn: () => adminGetOrganizationV2(organization_uuid),
		enabled: isEdit,
		refetchOnWindowFocus: false,
	});

	const { control, handleSubmit, reset } = useForm({
		defaultValues: {
			name: organization?.name || '',
			description: organization?.description || '',
			org_no: organization?.org_no || '',
			address: organization?.address || '',
			postal_code: organization?.postal_code || '',
			city: organization?.city || '',
			country: organization?.country || '',
			project_tags: organization?.project_tags || [],
		},
		values: organization,
	});

	const updateMutation = useMutation({
		mutationFn: data =>
			adminUpdateOrganizationV2(organization_uuid, normalizeDetails(data)),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organization', organization_uuid],
			});
			setEditing(false);
		},
	});

	const createMutation = useMutation({
		mutationFn: data => adminCreateOrganizationV2(normalizeDetails(data)),
		onSuccess: created => {
			// Land on the new org's edit page so products/members can be set.
			// Replace so Back goes to the list, not a stale create form.
			if (created?.uuid) {
				navigate(`/admin/organizations/${created.uuid}`, {
					replace: true,
				});
			} else {
				navigate(-1);
			}
		},
	});

	const onSubmitDetails = data => {
		if (isEdit) {
			updateMutation.mutate(data);
		} else {
			createMutation.mutate(data);
		}
	};

	const cancelEdit = () => {
		reset();
		setEditing(false);
	};

	if (isError)
		return (
			<AdminView title="Error loading organization">
				<Alert variant="danger">
					<p>
						Organization could not be loaded. Error:{' '}
						{error?.message}
					</p>
				</Alert>
			</AdminView>
		);

	const title = isEdit
		? `Organization: ${organization?.name ?? ''}`
		: 'Create organization';

	const renderDetailsForm = () => (
		<form onSubmit={handleSubmit(onSubmitDetails)}>
			<OrganizationDetailsFormFields control={control} />

			<Controller
				name="project_tags"
				control={control}
				render={({ field }) => (
					<Select
						{...field}
						value={field.value?.map(t => ({
							value: t,
							label: t,
						}))}
						onChange={e => field.onChange(e.map(tag => tag.label))}
						id="organization-tags"
						label="Project tags"
						noOptionsMessage={() => 'Start typing to add a tag'}
						helperText="By assigning project tags, users in this organization will be limited to add only these tags to their projects"
						isMulti
						creatable
					/>
				)}
			/>

			<SectionActions>
				{isEdit ? (
					<Button
						type="button"
						variant="secondary"
						onClick={cancelEdit}>
						Cancel
					</Button>
				) : (
					<Button
						type="button"
						variant="secondary"
						onClick={() => navigate(-1)}>
						Back
					</Button>
				)}
				<Button
					type="submit"
					variant="success"
					disabled={
						isLoading ||
						updateMutation.isPending ||
						createMutation.isPending
					}>
					{isEdit
						? updateMutation.isPending
							? 'Saving...'
							: 'Save details'
						: createMutation.isPending
							? 'Creating...'
							: 'Create organization'}
				</Button>
			</SectionActions>
		</form>
	);

	const renderDetailsReadOnly = () => (
		<DetailGrid>
			{DETAIL_FIELDS.map(({ key, label }) => (
				<div style={{ display: 'contents' }} key={key}>
					<dt>{label}</dt>
					<dd>{organization?.[key] ?? <span>—</span>}</dd>
				</div>
			))}
			<dt>Project tags</dt>
			<dd>
				{organization?.project_tags?.length
					? organization.project_tags.join(', ')
					: '—'}
			</dd>
		</DetailGrid>
	);

	return (
		<AdminView
			title={title}
			breadcrumbs={[
				{ title: 'Organizations', link: '/admin/organizations' },
				{
					title: organization?.name || 'Create organization',
					link: `/admin/organizations/${organization?.uuid || 'create'}`,
				},
			]}>
			{isEdit && (
				<QuickLinks>
					<Link to={adminProjectsUrl(organization_uuid)}>
						Projects
					</Link>
					{canViewBilling && (
						<Link to={adminBillingUrl(organization_uuid)}>
							Billing
						</Link>
					)}
				</QuickLinks>
			)}

			{/* ---------- Details ---------- */}
			<Section>
				<SectionHeader>
					<h2>Details</h2>
					{isEdit && !editing && (
						<Button
							size="sm"
							variant="success"
							onClick={() => setEditing(true)}>
							Edit
						</Button>
					)}
				</SectionHeader>

				{updateMutation.isError && (
					<Alert variant="danger">
						Could not update organization:{' '}
						{updateMutation.error?.message}
					</Alert>
				)}
				{createMutation.isError && (
					<Alert variant="danger">
						Could not create organization:{' '}
						{createMutation.error?.message}
					</Alert>
				)}

				{editing ? renderDetailsForm() : renderDetailsReadOnly()}
			</Section>

			{/* ---------- Edit-only action sections (later increments) ---------- */}
			{/* Hidden while the Details form is open: each section writes the
			    returned org to the query cache, which resets the form via
			    `values` and wipes unsaved edits. */}
			{isEdit && organization && !editing && (
				<>
					<ProductsSection
						orgUuid={organization_uuid}
						entitlements={organization.entitlements ?? []}
						wmsWorkspaceName={organization.wms_workspace_name ?? ''}
					/>
					<MembersSection
						orgUuid={organization_uuid}
						members={organization.members ?? []}
					/>
					<DangerZone
						orgUuid={organization_uuid}
						orgName={organization.name}
						deleted={organization.deleted}
					/>
				</>
			)}
		</AdminView>
	);
};

const WMS_PRODUCT = 'product.wms';

// Display label for a product value (e.g. 'product.dsp' -> 'DSP'), matching the toggle.
const productLabel = value =>
	PRODUCTS.find(p => p.value === value)?.label ?? value;

/**
 * Products section: a compact toggle per product. Toggling fires an immediate
 * enable/disable call; the server returns the updated org, which we push back
 * into the query cache so the switches reflect persisted state (and revert if a
 * call fails). WMS additionally exposes a workspace-name parameter that persists
 * via the org PATCH (two-step toggle + save) since it is not part of the product
 * toggle schema.
 */
const ProductsSection = ({ orgUuid, entitlements, wmsWorkspaceName }) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();

	// Which product is mid-request. Any in-flight toggle or WMS save disables
	// all switches and the WMS Save, so only one request writes to the org
	// cache at a time and responses can't land out of order.
	const [pending, setPending] = useState(null);
	const [wmsName, setWmsName] = useState(wmsWorkspaceName);

	// Resync the local input when the persisted value changes (e.g. a refetch
	// after invalidateQueries or another admin's edit) so a later Save doesn't
	// overwrite the newer value with a stale one.
	useEffect(() => {
		setWmsName(wmsWorkspaceName);
	}, [wmsWorkspaceName]);

	const isEnabled = product => entitlements.includes(product);
	const wmsEnabled = isEnabled(WMS_PRODUCT);

	const refreshOrg = updatedOrg => {
		queryClient.setQueryData(['organization', orgUuid], updatedOrg);
	};

	const toggleProduct = async (product, enable) => {
		setPending(product);
		try {
			const updatedOrg = enable
				? await adminEnableOrganizationProductV2(orgUuid, product)
				: await adminDisableOrganizationProductV2(orgUuid, product);
			refreshOrg(updatedOrg);
			addToast({
				id: `product-${product}`,
				bg: 'success',
				title: `${enable ? 'Enabled' : 'Disabled'} ${productLabel(product)}`,
			});
		} catch (err) {
			console.error('Failed to toggle product', product, err);
			addToast({
				id: `product-${product}-error`,
				bg: 'danger',
				title: `Failed to ${enable ? 'enable' : 'disable'} ${productLabel(product)}`,
			});
			// Re-fetch to resync the switch with server truth.
			queryClient.invalidateQueries({
				queryKey: ['organization', orgUuid],
			});
		} finally {
			setPending(null);
		}
	};

	const saveWmsName = useMutation({
		mutationFn: () =>
			adminUpdateOrganizationV2(orgUuid, {
				wms_workspace_name: wmsName || null,
			}),
		onSuccess: updatedOrg => {
			refreshOrg(updatedOrg);
			addToast({
				id: 'wms-workspace-saved',
				bg: 'success',
				title: 'WMS workspace name saved',
			});
		},
		onError: err => {
			addToast({
				id: 'wms-workspace-error',
				bg: 'danger',
				title: `Could not save WMS workspace name: ${err?.message ?? 'error'}`,
			});
		},
	});

	return (
		<Section>
			<SectionHeader>
				<h2>Products</h2>
			</SectionHeader>

			{PRODUCTS.filter(product => !product.isDefault).map(product => {
				const enabled = isEnabled(product.value);
				return (
					<div key={product.value}>
						<ProductRow>
							<Toggle
								type="switch"
								variant="dark"
								id={`product-${product.value}`}
								checked={enabled}
								disabled={
									pending !== null || saveWmsName.isPending
								}
								inline
								onChange={e =>
									toggleProduct(
										product.value,
										e.target.checked
									)
								}
							/>
							<ProductName>{product.label}</ProductName>
						</ProductRow>

						{/* WMS parameter — only when the WMS product is enabled. */}
						{product.value === WMS_PRODUCT && wmsEnabled && (
							<WmsParam>
								<Input
									id="wms-workspace-name"
									value={wmsName || ''}
									onChange={e => setWmsName(e.target.value)}
									type="text"
									label="WMS workspace name"
									helperText="GeoServer workspace for this organization's WMS. Without it, projects are not published to the map service."
								/>
								<Button
									variant="success"
									disabled={
										pending !== null ||
										saveWmsName.isPending
									}
									onClick={() => saveWmsName.mutate()}>
									{saveWmsName.isPending ? 'Saving...' : 'Save'}
								</Button>
							</WmsParam>
						)}
					</div>
				);
			})}
		</Section>
	);
};

const ROLE_OPTIONS = Object.keys(ROLES).map(r => ({
	label: r,
	value: ROLES[r],
}));

// Default access for a newly added member. Access is not surfaced in the UI
// (org-level entitlements govern capability); the member endpoint still
// requires the field, so we send the baseline 'create' token. Existing members
// keep whatever access they already have on role changes.
const DEFAULT_MEMBER_ACCESS = ['create'];

/**
 * Members section: lists org members with a role control and remove action, and
 * an "add member" user search. Each change fires an immediate members
 * sub-resource call (PUT/DELETE) that returns the updated org, which we push
 * into the query cache. Per-member access toggles are intentionally omitted —
 * capability is governed by org-level entitlements, and the access tokens on
 * the user record are not currently used for org work.
 */
const MembersSection = ({ orgUuid, members }) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const { confirm } = useConfirm();

	// user_uuid mid-request. Any in-flight request disables all member
	// controls so responses can't land in the org cache out of order.
	const [pending, setPending] = useState(null);
	const [addOpen, setAddOpen] = useState(false);
	// The user picked in the search but not yet committed (added on button press).
	const [selectedUser, setSelectedUser] = useState(null);
	// Bumped after each successful add to reset the async Select's input.
	const [pickerKey, setPickerKey] = useState(0);

	const refreshOrg = updatedOrg =>
		queryClient.setQueryData(['organization', orgUuid], updatedOrg);

	const resyncOrg = () =>
		queryClient.invalidateQueries({ queryKey: ['organization', orgUuid] });

	const putMember = async (userUuid, role, access) => {
		setPending(userUuid);
		try {
			const updatedOrg = await adminPutOrganizationMemberV2(
				orgUuid,
				userUuid,
				{ role, access }
			);
			refreshOrg(updatedOrg);
			addToast({
				id: `member-${userUuid}`,
				bg: 'success',
				title: 'Member saved',
			});
			return true;
		} catch (err) {
			console.error('Failed to save member', userUuid, err);
			addToast({
				id: `member-${userUuid}-error`,
				bg: 'danger',
				title: 'Failed to save member',
			});
			resyncOrg();
			return false;
		} finally {
			setPending(null);
		}
	};

	const removeMember = async member => {
		const ok = await confirm({
			title: 'Remove member',
			message: (
				<>
					Remove <strong>{member.user_email}</strong> from this
					organization?
				</>
			),
		});
		if (!ok) return;

		setPending(member.user_uuid);
		try {
			const updatedOrg = await adminDeleteOrganizationMemberV2(
				orgUuid,
				member.user_uuid
			);
			refreshOrg(updatedOrg);
			addToast({
				id: `member-remove-${member.user_uuid}`,
				bg: 'success',
				title: 'Member removed',
			});
		} catch (err) {
			console.error('Failed to remove member', member.user_uuid, err);
			addToast({
				id: `member-remove-${member.user_uuid}-error`,
				bg: 'danger',
				title: 'Failed to remove member',
			});
			resyncOrg();
		} finally {
			setPending(null);
		}
	};

	// Debounced user search for the "add member" picker. Excludes existing members.
	const loadUserOptions = useDebounce(async (inputValue, callback) => {
		try {
			const data = await adminGetUsersV2({ search: inputValue });
			const existing = new Set(members.map(m => m.user_uuid));
			const options = (data?.users ?? [])
				.filter(u => !existing.has(u.uuid))
				.map(u => ({ value: u.uuid, label: u.email }));
			callback(options);
		} catch (err) {
			console.error('User search failed', err);
			callback([]);
		}
	}, 300);

	const handleAddMember = async () => {
		if (!selectedUser?.value) return;
		// New members are added at user role with the baseline access.
		const ok = await putMember(
			selectedUser.value,
			ROLES.User,
			DEFAULT_MEMBER_ACCESS
		);
		// Keep the picker open but cleared so another member can be added.
		if (ok) {
			setSelectedUser(null);
			setPickerKey(k => k + 1);
		}
	};

	const cancelAdd = () => {
		setSelectedUser(null);
		setAddOpen(false);
	};

	return (
		<Section>
			<SectionHeader>
				<h2>Members</h2>
			</SectionHeader>

			{members.length === 0 && (
				<p className="text-muted mb-0">No members yet.</p>
			)}

			{members.map(member => (
				<MemberRow key={member.user_uuid}>
					<MemberEmail>{member.user_email}</MemberEmail>
					<RoleSelect>
						<Select
							id={`member-role-${member.user_uuid}`}
							value={ROLE_OPTIONS.find(
								o => o.value === member.role
							)}
							options={ROLE_OPTIONS}
							isDisabled={pending !== null}
							noWrap
							onChange={opt =>
								putMember(
									member.user_uuid,
									opt.value,
									member.access ?? DEFAULT_MEMBER_ACCESS
								)
							}
						/>
					</RoleSelect>
					<Button
						variant="outline-danger"
						size="sm"
						disabled={pending !== null}
						onClick={() => removeMember(member)}>
						Remove
					</Button>
				</MemberRow>
			))}

			{!addOpen ? (
				<AddMemberBar>
					<Button
						variant="success"
						size="sm"
						onClick={() => setAddOpen(true)}>
						+ Add member
					</Button>
				</AddMemberBar>
			) : (
				<AddMemberRow>
					<Select
						key={pickerKey}
						id="add-member-search"
						label="Add member"
						loadOptions={loadUserOptions}
						noOptionsMessage={() => 'Start typing to search users'}
						onChange={setSelectedUser}
						value={selectedUser}
						async
					/>
					<Button
						variant="success"
						disabled={!selectedUser || pending !== null}
						onClick={handleAddMember}>
						Add
					</Button>
					<Button variant="secondary" onClick={cancelAdd}>
						Cancel
					</Button>
				</AddMemberRow>
			)}
		</Section>
	);
};

/**
 * Danger zone: archive (soft-delete) the organization. Guarded by a confirm
 * dialog. Archiving is reversible server-side (deleted flag), but it removes the
 * org from normal listings, so it is treated as a destructive action here.
 */
const DangerZone = ({ orgUuid, orgName, deleted }) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const { confirm } = useConfirm();

	const archive = useMutation({
		mutationFn: () => adminDeleteOrganizationV2(orgUuid),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['organization', orgUuid],
			});
			queryClient.invalidateQueries({ queryKey: ['admin_organizations'] });
			addToast({
				id: 'org-archived',
				bg: 'success',
				title: 'Organization archived',
			});
		},
		onError: err => {
			addToast({
				id: 'org-archive-error',
				bg: 'danger',
				title: `Could not archive organization: ${err?.message ?? 'error'}`,
			});
		},
	});

	const handleArchive = async () => {
		const ok = await confirm({
			title: 'Archive organization',
			message: (
				<>
					Archive <strong>{orgName}</strong>? It will be removed from
					the active organization list.
				</>
			),
		});
		if (ok) archive.mutate();
	};

	return (
		<DangerSection>
			<SectionHeader>
				<h2>Danger zone</h2>
			</SectionHeader>
			<DangerBar>
				{deleted ? (
					<p className="text-muted">
						This organization is archived.
					</p>
				) : (
					<p>Archive this organization.</p>
				)}
				<Button
					variant="danger"
					disabled={deleted || archive.isPending}
					onClick={handleArchive}>
					{archive.isPending ? 'Archiving...' : 'Archive'}
				</Button>
			</DangerBar>
		</DangerSection>
	);
};

export default Organization;